/**
 * Night Schedule Visualization
 *
 * Modern timeline visualization using Apache ECharts for astronomy planning.
 * Replaces the old Plotly-based Gantt chart with a cleaner, faster interface.
 *
 * @module nightSchedule
 * @requires echarts
 */

import { AstroImagingCalculator } from '../astroCalculator.js';
import { chooseDisplayName } from '../displayNames.js';

export class NightScheduleVisualization {
    constructor(containerId, options = {}) {
        this.containerId = containerId;
        this.options = {
            showRecommendations: options.showRecommendations !== false,
            enableComparison: options.enableComparison || false,
            mobile: options.mobile || (typeof window !== 'undefined' && window.innerWidth < 768),
            astroCalculator: options.astroCalculator || null,
            ...options
        };

        this.chart = null;
        this.currentData = null;
        this.isInitialized = false;
        this.astroCalc = this.options.astroCalculator || new AstroImagingCalculator();

        console.log('NightScheduleVisualization instance created', this.options);
    }

    /**
     * Initialize the visualization
     * @returns {Promise<boolean>} Success status
     */
    async initialize() {
        console.log('Initializing Night Schedule Visualization...');

        // Check if echarts is loaded
        if (typeof echarts === 'undefined') {
            console.error('ECharts library not loaded!');
            return false;
        }

        // Get container
        const container = document.getElementById(this.containerId);
        if (!container) {
            console.error(`Container ${this.containerId} not found`);
            return false;
        }

        // Initialize ECharts instance
        try {
            this.chart = echarts.init(container, this.options.theme);

            // Set up responsive resize
            window.addEventListener('resize', () => {
                if (this.chart) {
                    this.chart.resize();
                }
            });

            // Set up event listeners
            this.setupEventListeners();

            this.isInitialized = true;
            console.log('Night Schedule Visualization initialized successfully');
            return true;

        } catch (error) {
            console.error('Failed to initialize ECharts:', error);
            return false;
        }
    }

    /**
     * Create timeline from object data
     * @param {Array} objects - Ranked objects with visibility data
     * @param {Object} params - Observation parameters
     */
    createTimeline(objects, params) {
        console.log('createTimeline called with', objects?.length, 'objects');
        console.log('Sample object:', objects?.[0]);
        console.log('Params:', params);

        if (!this.isInitialized) {
            console.error('Visualization not initialized. Call initialize() first.');
            console.error('isInitialized:', this.isInitialized, 'chart:', this.chart);
            return;
        }

        if (!objects || objects.length === 0) {
            console.log('No objects to display, showing empty timeline');
            this.showEmptyTimeline();
            return;
        }

        if (!params || !params.observationDate) {
            console.error('Invalid params - missing observationDate:', params);
            return;
        }

        // Store current data
        this.currentData = { objects, params };

        // Build and render chart
        this.renderTimeline();
    }

    /**
     * Render the timeline chart
     */
    renderTimeline() {
        console.log('Rendering timeline...');

        if (!this.currentData) {
            console.warn('No data to render');
            return;
        }

        const { objects, params } = this.currentData;
        console.log('renderTimeline: objects count:', objects.length);

        // Hide loading placeholder
        const loadingPlaceholder = document.getElementById('timelineLoading');
        if (loadingPlaceholder) {
            loadingPlaceholder.style.display = 'none';
            console.log('Loading placeholder hidden');
        }

        // Filter objects based on options
        const filteredObjects = this.filterObjects(objects);
        console.log('renderTimeline: filteredObjects count:', filteredObjects.length);

        // Build chart option
        const option = this.buildChartOption(filteredObjects, params);
        console.log('renderTimeline: series data length:', option.series?.[0]?.data?.length);
        console.log('renderTimeline: series data:', option.series?.[0]?.data);

        // Render chart
        this.chart.setOption(option, true);

        // Force resize after rendering to ensure proper display
        setTimeout(() => {
            if (this.chart) {
                this.chart.resize();
                console.log('Chart resized after render');
            }
        }, 100);

        console.log('Timeline rendered successfully');
    }

    /**
     * Filter objects based on current options
     * @param {Array} objects - All objects
     * @returns {Array} Filtered and sorted objects
     */
    filterObjects(objects) {
        let filteredObjects = [...objects]; // Create copy to avoid mutating original

        // Sort by grouping first
        if (this.options.grouping) {
            filteredObjects = this.sortByGrouping(filteredObjects, this.options.grouping);
        }

        // Then filter by count
        filteredObjects = this.filterByCount(filteredObjects, this.options.objectCount);

        return filteredObjects;
    }

    /**
     * Filter objects by count
     * @param {Array} objects - Objects to filter
     * @param {string} count - Count setting (top5, top10, top20, all)
     * @returns {Array} Filtered objects
     */
    filterByCount(objects, count) {
        switch(count) {
            case 'top5': return objects.slice(0, 5);
            case 'top10': return objects.slice(0, 10);
            case 'top20': return objects.slice(0, 20);
            case 'all': return objects;
            default: return objects.slice(0, 10);
        }
    }

    /**
     * Sort objects by grouping criteria
     * @param {Array} objects - Objects to sort
     * @param {string} grouping - Grouping method (score, type, duration, altitude)
     * @returns {Array} Sorted objects
     */
    sortByGrouping(objects, grouping) {
        const sorted = [...objects]; // Create copy to avoid mutating original

        switch(grouping) {
            case 'score':
                return sorted.sort((a, b) => {
                    const scoreA = a.score?.total || 0;
                    const scoreB = b.score?.total || 0;
                    return scoreB - scoreA;
                });

            case 'type':
                return sorted.sort((a, b) => {
                    const typeOrder = {
                        'galaxy': 1,
                        'nebula': 2,
                        'emission nebula': 2,
                        'reflection nebula': 2,
                        'planetary nebula': 3,
                        'dark nebula': 3,
                        'star cluster': 4,
                        'open cluster': 4,
                        'globular cluster': 4,
                        'double star': 5,
                        'star': 5
                    };
                    const orderA = typeOrder[a.object_type?.toLowerCase()] || 6;
                    const orderB = typeOrder[b.object_type?.toLowerCase()] || 6;
                    return orderA - orderB;
                });

            case 'duration':
                return sorted.sort((a, b) => {
                    const durationA = a.visibility?.totalDuration || 0;
                    const durationB = b.visibility?.totalDuration || 0;
                    return durationB - durationA;
                });

            case 'altitude':
                return sorted.sort((a, b) => {
                    const altitudeA = a.visibility?.peakAltitude || 0;
                    const altitudeB = b.visibility?.peakAltitude || 0;
                    return altitudeB - altitudeA;
                });

            default:
                return sorted;
        }
    }

    /**
     * Build complete chart option
     * @param {Array} objects - Objects to display
     * @param {Object} params - Observation parameters
     * @returns {Object} ECharts option
     */
    buildChartOption(objects, params) {
        console.log(`Building chart for ${objects.length} objects`);
        console.log('Sample object structure:', objects[0]);
        console.log('Sample visibility:', objects[0]?.visibility);
        console.log('Sample visiblePeriods:', objects[0]?.visibility?.visiblePeriods);

        // Calculate twilight times FIRST to determine chart range
        const twilight = this.astroCalc.calculateTwilightTimes(
            new Date(params.observationDate),
            params.latitude,
            params.longitude
        );

        // Start time: 30 minutes before civil twilight begins (evening)
        let startTime;
        if (twilight.civilEvening) {
            startTime = new Date(twilight.civilEvening);
            startTime.setMinutes(startTime.getMinutes() - 30);
        } else {
            // Fallback to 18:00 if no twilight data
            startTime = new Date(params.observationDate);
            startTime.setHours(18, 0, 0, 0);
        }

        // End time: 30 minutes after civil twilight ends (morning)
        let endTime;
        if (twilight.civilMorning) {
            endTime = new Date(twilight.civilMorning);
            endTime.setMinutes(endTime.getMinutes() + 30);

            // Ensure end time is on the next day if morning time
            const hour = endTime.getHours();
            if (hour >= 0 && hour < 12) {
                endTime.setFullYear(startTime.getFullYear(), startTime.getMonth(), startTime.getDate() + 1);
            }
        } else {
            // Fallback to 06:00 next day if no twilight data
            endTime = new Date(startTime);
            endTime.setDate(endTime.getDate() + 1);
            endTime.setHours(6, 0, 0, 0);
        }

        // Log the final chart timeline
        console.log(`Chart timeline: ${startTime.toLocaleTimeString()} → ${endTime.toLocaleTimeString()}`);

        // Calculate twilight and moon info for enhanced title
        const twilightInfo = this.calculateTwilightInfo(startTime, params);

        return {
            backgroundColor: '#0a0a0a',
            title: {
                text: 'Night Sky Schedule',
                subtext: twilightInfo,
                left: 'center',
                top: 10,
                textStyle: {
                    fontSize: 20,
                    fontWeight: '600',
                    color: '#e0e0e0'
                },
                subtextStyle: {
                    fontSize: 12,
                    color: '#b0b0b0',
                    lineHeight: 18
                }
            },
            tooltip: {
                trigger: 'item',
                formatter: (params) => this.formatTooltip(params),
                backgroundColor: 'rgba(10, 10, 10, 0.95)',
                borderColor: '#2a2a2a',
                borderWidth: 1,
                textStyle: {
                    color: '#b0b0b0'
                }
            },
            grid: {
                left: 160,
                right: 100,
                top: 100,
                bottom: 120,
                containLabel: true
            },
            xAxis: {
                type: 'time',
                min: startTime,
                max: endTime,
                axisLabel: {
                    formatter: '{HH}:{mm}',
                    color: '#b0b0b0',
                    fontSize: 11
                },
                axisLine: {
                    lineStyle: {
                        color: '#2a2a2a'
                    }
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: 'rgba(42, 42, 42, 0.8)',
                        type: 'dashed'
                    }
                }
            },
            yAxis: {
                type: 'category',
                data: objects.map(obj => this.getObjectLabel(obj)),
                axisLabel: {
                    width: 140,
                    overflow: 'truncate',
                    color: '#b0b0b0',
                    fontSize: 12,
                    rich: {
                        icon: {
                            fontSize: 14,
                            padding: [0, 4, 0, 0]
                        }
                    }
                },
                axisLine: {
                    lineStyle: {
                        color: '#2a2a2a'
                    }
                },
                inverse: true // Top to bottom
            },
            visualMap: {
                type: 'piecewise',
                show: true,
                dimension: 3, // altitude dimension
                orient: 'horizontal',
                left: 'center',
                bottom: 10,
                pieces: [
                    { min: 70, label: 'Excellent (>70°)', color: '#059669' },      // Vibrant green
                    { min: 55, max: 70, label: 'Very Good (55-70°)', color: '#0284c7' }, // Bright blue
                    { min: 40, max: 55, label: 'Good (40-55°)', color: '#ca8a04' },      // Golden yellow
                    { min: 30, max: 40, label: 'Fair (30-40°)', color: '#ea580c' },      // Orange
                    { max: 30, label: 'Poor (<30°)', color: '#dc2626' }                  // Red
                ],
                textStyle: {
                    color: '#b0b0b0',
                    fontSize: 11
                },
                itemWidth: 20,
                itemHeight: 14
            },
            dataZoom: [
                {
                    type: 'inside',
                    xAxisIndex: 0,
                    start: 0,
                    end: 100,
                    zoomLock: false,
                    moveOnMouseMove: true,
                    moveOnMouseWheel: true,
                    preventDefaultMouseMove: false
                },
                {
                    type: 'slider',
                    xAxisIndex: 0,
                    start: 0,
                    end: 100,
                    height: 20,
                    bottom: 60,
                    borderColor: '#2a2a2a',
                    backgroundColor: '#141414',
                    fillerColor: 'rgba(59, 130, 246, 0.3)',
                    handleStyle: {
                        color: '#3b82f6',
                        borderColor: '#2563eb'
                    },
                    textStyle: {
                        color: '#b0b0b0',
                        fontSize: 10
                    },
                    dataBackground: {
                        lineStyle: { color: '#3a3a3a' },
                        areaStyle: { color: '#2a2a2a' }
                    },
                    showDetail: true,
                    showDataShadow: true
                }
            ],
            series: [
                // Main visibility bars with twilight and moon zones
                {
                    type: 'custom',
                    renderItem: (params, api) => this.renderVisibilityBar(params, api),
                    encode: {
                        x: [0, 1], // start, end time
                        y: 2       // object index
                    },
                    data: this.prepareSeriesData(objects, params),
                    z: 2, // Draw on top of twilight/moon zones
                    markArea: {
                        silent: true,
                        data: [
                            // Twilight zones (blue, z:0)
                            ...this.createTwilightZones(startTime, endTime, objects.length, params),
                            // Moon visibility zones (amber, z:1)
                            ...this.createMoonVisibilityZones(startTime, endTime, params)
                        ]
                    },
                    markLine: {
                        silent: false,
                        symbol: 'none',
                        data: this.createMoonMarkers(startTime, endTime, params)
                    }
                }
            ]
        };
    }

    /**
     * Create twilight zone background using markArea
     * @param {Date} startTime - Observation start (18:00)
     * @param {Date} endTime - Observation end (06:00 next day)
     * @param {number} objectCount - Number of objects (for height)
     * @param {Object} params - Observation parameters (for location)
     * @returns {Object} markArea configuration for twilight zones
     */
    createTwilightZones(startTime, endTime, objectCount, params) {
        const markAreas = [];

        // Calculate actual twilight times based on location and date
        const twilight = this.astroCalc.calculateTwilightTimes(
            new Date(params.observationDate),
            params.latitude,
            params.longitude
        );

        console.log('Twilight times calculated:', twilight);

        // If we don't have twilight data (polar regions, etc.), return empty
        if (!twilight.civilEvening && !twilight.civilMorning) {
            console.warn('No twilight data available for this location/date');
            return [];
        }

        // Adjust twilight times to be within our chart's timeline (18:00 to 06:00 next day)
        // The chart starts at 18:00 on observation date and goes to 06:00 next morning
        const adjustTime = (time) => {
            if (!time) return null;

            const adjusted = new Date(time);
            const hour = adjusted.getHours();

            // If time is before 18:00, it's on the same date as chart start
            // If time is 18:00 or after, but before midnight, it's on chart start date
            // If time is after midnight (0-6), it's on the next day

            if (hour >= 0 && hour < 12) {
                // Morning time (0-12) - should be next day
                adjusted.setFullYear(startTime.getFullYear(), startTime.getMonth(), startTime.getDate() + 1);
            } else {
                // Evening time (12-24) - should be same day as start
                adjusted.setFullYear(startTime.getFullYear(), startTime.getMonth(), startTime.getDate());
            }

            return adjusted;
        };

        // Get sunset/sunrise
        // Sunset: approximately 30 mins before civil twilight ends
        const sunset = twilight.civilEvening ?
            new Date(adjustTime(twilight.civilEvening).getTime() - 30 * 60 * 1000) : null;

        // Adjust all twilight times
        const adjustedTwilight = {
            civilEvening: adjustTime(twilight.civilEvening),
            nauticalEvening: adjustTime(twilight.nauticalEvening),
            astronomicalEvening: adjustTime(twilight.astronomicalEvening),
            civilMorning: adjustTime(twilight.civilMorning),
            nauticalMorning: adjustTime(twilight.nauticalMorning),
            astronomicalMorning: adjustTime(twilight.astronomicalMorning)
        };

        const yMax = objectCount + 0.5;

        // Evening civil twilight (sunset to civil end) - Light blue
        if (sunset && adjustedTwilight.civilEvening) {
            console.log(`Evening Civil: ${sunset.toLocaleTimeString()} - ${adjustedTwilight.civilEvening.toLocaleTimeString()}`);
            markAreas.push([
                { xAxis: sunset.getTime(), itemStyle: { color: 'rgba(135, 206, 250, 0.15)' } },
                { xAxis: adjustedTwilight.civilEvening.getTime() }
            ]);
        }

        // Evening nautical twilight (civil end to nautical end) - Medium blue
        if (adjustedTwilight.civilEvening && adjustedTwilight.nauticalEvening) {
            console.log(`Evening Nautical: ${adjustedTwilight.civilEvening.toLocaleTimeString()} - ${adjustedTwilight.nauticalEvening.toLocaleTimeString()}`);
            markAreas.push([
                { xAxis: adjustedTwilight.civilEvening.getTime(), itemStyle: { color: 'rgba(100, 149, 237, 0.2)' } },
                { xAxis: adjustedTwilight.nauticalEvening.getTime() }
            ]);
        }

        // Evening astronomical twilight (nautical end to astronomical end) - Dark blue
        if (adjustedTwilight.nauticalEvening && adjustedTwilight.astronomicalEvening) {
            console.log(`Evening Astronomical: ${adjustedTwilight.nauticalEvening.toLocaleTimeString()} - ${adjustedTwilight.astronomicalEvening.toLocaleTimeString()}`);
            markAreas.push([
                { xAxis: adjustedTwilight.nauticalEvening.getTime(), itemStyle: { color: 'rgba(70, 130, 220, 0.25)' } },
                { xAxis: adjustedTwilight.astronomicalEvening.getTime() }
            ]);
        }

        // Morning astronomical twilight (astronomical start to nautical start) - Dark blue
        if (adjustedTwilight.astronomicalMorning && adjustedTwilight.nauticalMorning) {
            console.log(`Morning Astronomical: ${adjustedTwilight.astronomicalMorning.toLocaleTimeString()} - ${adjustedTwilight.nauticalMorning.toLocaleTimeString()}`);
            markAreas.push([
                { xAxis: adjustedTwilight.astronomicalMorning.getTime(), itemStyle: { color: 'rgba(70, 130, 220, 0.25)' } },
                { xAxis: adjustedTwilight.nauticalMorning.getTime() }
            ]);
        }

        // Morning nautical twilight (nautical start to civil start) - Medium blue
        if (adjustedTwilight.nauticalMorning && adjustedTwilight.civilMorning) {
            console.log(`Morning Nautical: ${adjustedTwilight.nauticalMorning.toLocaleTimeString()} - ${adjustedTwilight.civilMorning.toLocaleTimeString()}`);
            markAreas.push([
                { xAxis: adjustedTwilight.nauticalMorning.getTime(), itemStyle: { color: 'rgba(100, 149, 237, 0.2)' } },
                { xAxis: adjustedTwilight.civilMorning.getTime() }
            ]);
        }

        // Morning civil twilight (civil start to sunrise approximation) - Light blue
        const sunrise = adjustedTwilight.civilMorning ?
            new Date(adjustedTwilight.civilMorning.getTime() + 30 * 60 * 1000) : null;

        if (adjustedTwilight.civilMorning && sunrise) {
            console.log(`Morning Civil: ${adjustedTwilight.civilMorning.toLocaleTimeString()} - ${sunrise.toLocaleTimeString()}`);
            markAreas.push([
                { xAxis: adjustedTwilight.civilMorning.getTime(), itemStyle: { color: 'rgba(135, 206, 250, 0.15)' } },
                { xAxis: sunrise.getTime() }
            ]);
        }

        return markAreas;
    }

    /**
     * Create moon visibility zones using markArea
     * @param {Date} startTime - Observation start (18:00)
     * @param {Date} endTime - Observation end (06:00 next day)
     * @param {Object} params - Observation parameters (for location)
     * @returns {Array} markArea configuration for moon visibility
     */
    createMoonVisibilityZones(startTime, endTime, params) {
        const markAreas = [];

        // Calculate moon rise/set for the observation night
        const moonRiseSet = this.astroCalc.calculateMoonRiseSet(
            new Date(params.observationDate),
            params.latitude,
            params.longitude
        );

        console.log('Moon rise/set:', moonRiseSet);

        let { moonRise, moonSet } = moonRiseSet;

        // Adjust times to chart timeline
        const adjustTime = (time) => {
            if (!time) return null;
            const adjusted = new Date(time);
            const hour = adjusted.getHours();

            if (hour >= 0 && hour < 12) {
                // Morning time - next day
                adjusted.setFullYear(startTime.getFullYear(), startTime.getMonth(), startTime.getDate() + 1);
            } else {
                // Evening time - same day
                adjusted.setFullYear(startTime.getFullYear(), startTime.getMonth(), startTime.getDate());
            }

            return adjusted;
        };

        moonRise = adjustTime(moonRise);
        moonSet = adjustTime(moonSet);

        // Create moon visibility zone (amber overlay when moon is up)
        if (moonRise && moonSet) {
            // Moon rises and sets during our observation window
            if (moonRise < moonSet) {
                // Normal case: moon rises then sets
                console.log(`Moon visible: ${moonRise.toLocaleTimeString()} - ${moonSet.toLocaleTimeString()}`);
                markAreas.push([
                    { xAxis: moonRise.getTime(), itemStyle: { color: 'rgba(255, 193, 7, 0.12)' } }, // Amber
                    { xAxis: moonSet.getTime() }
                ]);
            } else {
                // Moon is already up at start, sets, then rises again
                console.log(`Moon sets at ${moonSet.toLocaleTimeString()}, rises at ${moonRise.toLocaleTimeString()}`);
                // Moon visible from start to set
                markAreas.push([
                    { xAxis: startTime.getTime(), itemStyle: { color: 'rgba(255, 193, 7, 0.12)' } },
                    { xAxis: moonSet.getTime() }
                ]);
                // Moon visible from rise to end
                markAreas.push([
                    { xAxis: moonRise.getTime(), itemStyle: { color: 'rgba(255, 193, 7, 0.12)' } },
                    { xAxis: endTime.getTime() }
                ]);
            }
        } else if (moonRise && !moonSet) {
            // Moon rises but doesn't set during observation
            console.log(`Moon rises at ${moonRise.toLocaleTimeString()}, remains up`);
            markAreas.push([
                { xAxis: moonRise.getTime(), itemStyle: { color: 'rgba(255, 193, 7, 0.12)' } },
                { xAxis: endTime.getTime() }
            ]);
        } else if (!moonRise && moonSet) {
            // Moon is already up, sets during observation
            console.log(`Moon up at start, sets at ${moonSet.toLocaleTimeString()}`);
            markAreas.push([
                { xAxis: startTime.getTime(), itemStyle: { color: 'rgba(255, 193, 7, 0.12)' } },
                { xAxis: moonSet.getTime() }
            ]);
        } else {
            // Check if moon is up throughout the night
            const midNight = new Date(startTime.getTime() + (endTime.getTime() - startTime.getTime()) / 2);
            const moonAlt = this.astroCalc.calculateMoonAltitude(midNight, params.latitude, params.longitude);
            if (moonAlt > 0) {
                console.log('Moon is up all night');
                markAreas.push([
                    { xAxis: startTime.getTime(), itemStyle: { color: 'rgba(255, 193, 7, 0.12)' } },
                    { xAxis: endTime.getTime() }
                ]);
            } else {
                console.log('Moon is down all night');
            }
        }

        return markAreas;
    }

    /**
     * Create moon rise/set markers
     * @param {Date} startTime - Observation start
     * @param {Date} endTime - Observation end
     * @param {Object} params - Observation parameters
     * @returns {Array} markLine data for moon events
     */
    createMoonMarkers(startTime, endTime, params) {
        const markers = [];

        const moonRiseSet = this.astroCalc.calculateMoonRiseSet(
            new Date(params.observationDate),
            params.latitude,
            params.longitude
        );

        let { moonRise, moonSet } = moonRiseSet;

        // Adjust times to chart timeline
        const adjustTime = (time) => {
            if (!time) return null;
            const adjusted = new Date(time);
            const hour = adjusted.getHours();

            if (hour >= 0 && hour < 12) {
                adjusted.setFullYear(startTime.getFullYear(), startTime.getMonth(), startTime.getDate() + 1);
            } else {
                adjusted.setFullYear(startTime.getFullYear(), startTime.getMonth(), startTime.getDate());
            }

            return adjusted;
        };

        moonRise = adjustTime(moonRise);
        moonSet = adjustTime(moonSet);

        // Add moon rise marker if within observation window
        if (moonRise && moonRise >= startTime && moonRise <= endTime) {
            markers.push({
                xAxis: moonRise.getTime(),
                label: {
                    formatter: '🌙↑',
                    position: 'insideEndTop',
                    fontSize: 14
                },
                lineStyle: {
                    color: '#fbbf24',
                    type: 'dashed',
                    width: 2
                }
            });
        }

        // Add moon set marker if within observation window
        if (moonSet && moonSet >= startTime && moonSet <= endTime) {
            markers.push({
                xAxis: moonSet.getTime(),
                label: {
                    formatter: '🌙↓',
                    position: 'insideEndTop',
                    fontSize: 14
                },
                lineStyle: {
                    color: '#fbbf24',
                    type: 'dashed',
                    width: 2
                }
            });
        }

        return markers;
    }

    /**
     * Get object label
     * @param {Object} obj - Object data
     * @returns {string} Object name (catalog designation, see displayNames.js)
     */
    getObjectLabel(obj) {
        return chooseDisplayName(obj);
    }

    /**
     * Get object type icon
     * @param {string} type - Object type
     * @returns {string} Emoji icon
     */
    getObjectTypeIcon(type) {
        const icons = {
            'galaxy': '🌌',
            'nebula': '🌫️',
            'emission nebula': '🌫️',
            'reflection nebula': '🌫️',
            'planetary nebula': '⭕',
            'dark nebula': '🌑',
            'star cluster': '⭐',
            'open cluster': '⭐',
            'globular cluster': '🔘',
            'double star': '⭐⭐',
            'star': '⭐'
        };
        return icons[type?.toLowerCase()] || '🔭';
    }

    /**
     * Calculate twilight and moon info for subtitle
     * @param {Date} date - Observation date
     * @param {Object} params - Observation parameters
     * @returns {string} Formatted info string
     */
    calculateTwilightInfo(date, params) {
        // Calculate actual twilight times using astronomy-engine
        const twilight = this.astroCalc.calculateTwilightTimes(
            new Date(params.observationDate),
            params.latitude,
            params.longitude
        );

        // Format times
        const twilightEnd = twilight.astronomicalEvening ?
            twilight.astronomicalEvening.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) :
            'N/A';

        const dawn = twilight.astronomicalMorning ?
            twilight.astronomicalMorning.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) :
            'N/A';

        // Calculate moon phase and illumination
        const moonInfo = this.astroCalc.calculateMoonIllumination(new Date(params.observationDate));
        const moonPhase = moonInfo.phase;
        const moonIllum = Math.round(moonInfo.illumination * 100);

        return `Twilight ends: ${twilightEnd}  •  Moon: ${moonPhase} (${moonIllum}%)  •  Dawn: ${dawn}`;
    }

    /**
     * Prepare series data from objects
     * @param {Array} objects - Objects with visibility data
     * @param {Object} params - Observation parameters
     * @returns {Array} Series data
     */
    prepareSeriesData(objects, params) {
        const data = [];

        objects.forEach((obj, index) => {
            const periods = obj.visibility?.visiblePeriods || [];

            if (periods.length === 0) {
                console.log(`Object ${obj.name} has no visible periods`);
                return;
            }
            console.log(`Object ${obj.name} has ${periods.length} visible periods`);

            // Group continuous periods
            let startTime = new Date(periods[0].time);
            let lastTime = startTime;
            let sumAltitude = periods[0].altitude;
            let count = 1;

            periods.forEach((period, i) => {
                const currentTime = new Date(period.time);
                const timeDiff = (currentTime - lastTime) / (1000 * 60); // minutes

                // If gap > 30 minutes, create new bar segment
                if (timeDiff > 30 && i > 0) {
                    const avgAltitude = sumAltitude / count;
                    data.push([
                        startTime.getTime(),
                        lastTime.getTime(),
                        index,
                        avgAltitude,
                        this.getObjectLabel(obj)
                    ]);

                    // Start new segment
                    startTime = currentTime;
                    sumAltitude = period.altitude;
                    count = 1;
                } else {
                    sumAltitude += period.altitude;
                    count++;
                }

                lastTime = currentTime;

                // Handle last period
                if (i === periods.length - 1) {
                    const avgAltitude = sumAltitude / count;
                    data.push([
                        startTime.getTime(),
                        lastTime.getTime(),
                        index,
                        avgAltitude,
                        this.getObjectLabel(obj)
                    ]);
                }
            });
        });

        console.log(`prepareSeriesData: Returning ${data.length} data points`);
        console.log('First 3 data points:', data.slice(0, 3));
        return data;
    }

    /**
     * Render custom visibility bar
     * @param {Object} params - Render parameters
     * @param {Object} api - ECharts API
     * @returns {Object} Render item
     */
    renderVisibilityBar(params, api) {
        const categoryIndex = api.value(2);
        const start = api.coord([api.value(0), categoryIndex]);
        const end = api.coord([api.value(1), categoryIndex]);
        const height = api.size([0, 1])[1] * 0.7;

        const altitude = api.value(3);
        const color = this.getQualityColor(altitude);

        // Create subtle gradient for depth
        const gradientColor = {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
                { offset: 0, color: this.lightenColor(color, 10) },
                { offset: 0.5, color: color },
                { offset: 1, color: this.darkenColor(color, 10) }
            ]
        };

        return {
            type: 'rect',
            shape: {
                x: start[0],
                y: start[1] - height / 2,
                width: Math.max(end[0] - start[0], 3),
                height: height,
                r: 3 // Rounded corners
            },
            style: {
                fill: gradientColor,
                stroke: this.darkenColor(color, 20),
                lineWidth: 1,
                shadowBlur: 4,
                shadowColor: 'rgba(0, 0, 0, 0.1)',
                shadowOffsetY: 2
            }
        };
    }

    /**
     * Lighten a hex color
     * @param {string} color - Hex color
     * @param {number} percent - Percent to lighten
     * @returns {string} Lightened color
     */
    lightenColor(color, percent) {
        const num = parseInt(color.replace('#', ''), 16);
        const amt = Math.round(2.55 * percent);
        const R = Math.min(255, (num >> 16) + amt);
        const G = Math.min(255, ((num >> 8) & 0x00FF) + amt);
        const B = Math.min(255, (num & 0x0000FF) + amt);
        return '#' + ((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1);
    }

    /**
     * Darken a hex color
     * @param {string} color - Hex color
     * @param {number} percent - Percent to darken
     * @returns {string} Darkened color
     */
    darkenColor(color, percent) {
        const num = parseInt(color.replace('#', ''), 16);
        const amt = Math.round(2.55 * percent);
        const R = Math.max(0, (num >> 16) - amt);
        const G = Math.max(0, ((num >> 8) & 0x00FF) - amt);
        const B = Math.max(0, (num & 0x0000FF) - amt);
        return '#' + ((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1);
    }

    /**
     * Get quality color based on altitude
     * @param {number} altitude - Altitude in degrees
     * @returns {string} Color string
     */
    getQualityColor(altitude) {
        if (altitude >= 70) return '#059669'; // Excellent - vibrant green
        if (altitude >= 55) return '#0284c7'; // Very good - bright blue
        if (altitude >= 40) return '#ca8a04'; // Good - golden yellow
        if (altitude >= 30) return '#ea580c'; // Fair - orange
        return '#dc2626'; // Poor - red
    }

    /**
     * Format tooltip content
     * @param {Object} params - Tooltip parameters
     * @returns {string} HTML content
     */
    formatTooltip(params) {
        const data = params.data;
        const objName = data[4];
        const startTime = new Date(data[0]);
        const endTime = new Date(data[1]);
        const altitude = data[3].toFixed(1);

        const duration = (endTime - startTime) / (1000 * 60 * 60); // hours

        // Calculate moon info for the midpoint of this visibility window
        const midTime = new Date((startTime.getTime() + endTime.getTime()) / 2);

        let moonInfo = '';
        if (this.currentData && this.currentData.params) {
            const moonAlt = this.astroCalc.calculateMoonAltitude(
                midTime,
                this.currentData.params.latitude,
                this.currentData.params.longitude
            );

            const moonIllum = this.astroCalc.calculateMoonIllumination(midTime);

            // Calculate moon interference level
            let interference = 0;
            if (moonAlt > 0) {
                const altitudeFactor = Math.min(moonAlt / 60, 1.0);
                const phaseFactor = moonIllum.illumination;
                interference = altitudeFactor * phaseFactor;
            }

            const interferenceLevel = interference < 0.2 ? 'Low ✅' :
                                     interference < 0.5 ? 'Medium ⚠️' : 'High ⛔';

            const interferenceColor = interference < 0.2 ? '#10b981' :
                                     interference < 0.5 ? '#f59e0b' : '#ef4444';

            moonInfo = `
            <br/>
            <strong>Moon Conditions:</strong><br/>
            🌙 Altitude: ${moonAlt > 0 ? moonAlt.toFixed(1) + '°' : 'Below horizon'}<br/>
            💡 Phase: ${moonIllum.phase} (${Math.round(moonIllum.illumination * 100)}%)<br/>
            <span style="color: ${interferenceColor};">🔆 Interference: ${interferenceLevel}</span>`;
        }

        return `
            <strong>${objName}</strong><br/>
            <hr style="margin: 5px 0;"/>
            ⏰ ${startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            - ${endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}<br/>
            ⏱️ Duration: ${duration.toFixed(1)} hours<br/>
            📈 Avg Altitude: ${altitude}°${moonInfo}
        `;
    }

    /**
     * Update timeline with new options
     * @param {Object} options - Options to update
     */
    updateTimeline(options) {
        this.options = { ...this.options, ...options };

        if (this.currentData) {
            this.renderTimeline();
        }
    }

    /**
     * Zoom the timeline by a factor
     * @param {number} factor - Zoom factor (>1 = zoom in, <1 = zoom out)
     */
    zoomTimeline(factor) {
        if (!this.chart) return;

        const currentOption = this.chart.getOption();
        const currentZoom = currentOption.dataZoom?.[0] || { start: 0, end: 100 };

        const currentStart = currentZoom.start || 0;
        const currentEnd = currentZoom.end || 100;
        const range = currentEnd - currentStart;
        const newRange = Math.max(10, Math.min(100, range / factor)); // Clamp between 10% and 100%
        const center = (currentStart + currentEnd) / 2;

        this.chart.setOption({
            dataZoom: [{
                type: 'inside',
                xAxisIndex: 0,
                start: Math.max(0, center - newRange / 2),
                end: Math.min(100, center + newRange / 2),
                zoomLock: false,
                moveOnMouseMove: true,
                moveOnMouseWheel: true,
                preventDefaultMouseMove: false
            }]
        });

        console.log(`Zoomed timeline by ${factor}x (range: ${newRange.toFixed(1)}%)`);
    }

    /**
     * Reset zoom to full timeline view
     */
    resetZoom() {
        if (!this.chart) return;

        this.chart.setOption({
            dataZoom: [{
                type: 'inside',
                xAxisIndex: 0,
                start: 0,
                end: 100,
                zoomLock: false,
                moveOnMouseMove: true,
                moveOnMouseWheel: true,
                preventDefaultMouseMove: false
            }]
        });

        console.log('Reset timeline zoom');
    }

    /**
     * Shift time view horizontally
     * @param {number} hours - Hours to shift (negative = left, positive = right)
     */
    shiftTimeView(hours) {
        if (!this.chart) return;

        const totalHours = 12; // Typical 18:00 to 06:00 = 12 hours
        const shiftPercent = (hours / totalHours) * 100;

        const currentOption = this.chart.getOption();
        const currentZoom = currentOption.dataZoom?.[0] || { start: 0, end: 100 };

        const currentStart = currentZoom.start || 0;
        const currentEnd = currentZoom.end || 100;
        const range = currentEnd - currentStart;

        let newStart = currentStart + shiftPercent;
        let newEnd = currentEnd + shiftPercent;

        // Clamp to valid range
        if (newStart < 0) {
            newStart = 0;
            newEnd = range;
        } else if (newEnd > 100) {
            newEnd = 100;
            newStart = 100 - range;
        }

        this.chart.setOption({
            dataZoom: [{
                type: 'inside',
                xAxisIndex: 0,
                start: newStart,
                end: newEnd,
                zoomLock: false,
                moveOnMouseMove: true,
                moveOnMouseWheel: true,
                preventDefaultMouseMove: false
            }]
        });

        console.log(`Shifted timeline by ${hours} hours`);
    }

    /**
     * Set up event listeners
     */
    setupEventListeners() {
        if (!this.chart) return;

        // Click handler
        this.chart.on('click', (params) => {
            if (params.componentType === 'series') {
                const objectName = params.data[4];
                this.highlightObjectInResults(objectName);
            }
        });
    }

    /**
     * Highlight object in results table
     * @param {string} objectName - Object name to highlight
     */
    highlightObjectInResults(objectName) {
        // Remove existing highlights
        document.querySelectorAll('.table-highlight').forEach(row => {
            row.classList.remove('table-highlight');
        });

        // Find and highlight matching row
        const tableRows = document.querySelectorAll('#resultsTableBody tr');
        tableRows.forEach(row => {
            const nameCell = row.querySelector('td:nth-child(2)');
            if (nameCell && nameCell.textContent.includes(objectName)) {
                row.classList.add('table-highlight');
                row.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        });
    }

    /**
     * Show empty timeline message
     */
    showEmptyTimeline() {
        const loadingPlaceholder = document.getElementById('timelineLoading');
        if (loadingPlaceholder) {
            loadingPlaceholder.style.display = 'flex';
            loadingPlaceholder.querySelector('h5').textContent = 'No Timeline Data';
            loadingPlaceholder.querySelector('p').textContent = 'No objects found to display';
            console.log('Showing empty timeline placeholder');
        }
    }

    /**
     * Clear the timeline without destroying the instance
     */
    clear() {
        if (this.chart) {
            this.chart.clear();
        }
        this.currentData = null;
        console.log('Timeline cleared');
    }

    /**
     * Destroy the visualization and clean up
     */
    destroy() {
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
        this.currentData = null;
        this.isInitialized = false;
    }
}

export default NightScheduleVisualization;
